import { useEffect, useRef } from 'react';
import { CalendarDays, ChevronRight, X } from 'lucide-react';

import type { DailyPulseDay } from 'services/api';

import './DailyPulseMissedDays.css';

interface DailyPulseMissedDaysProps {
  days: DailyPulseDay[];
  loadingDate: string;
  failedDate: string;
  onSelect: (date: string) => void;
  onCancel: () => void;
}

const formatDate = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  }).format(new Date(year, month - 1, day));
};

export const DailyPulseMissedDays = ({
  days, loadingDate, failedDate, onSelect, onCancel,
}: DailyPulseMissedDaysProps) => {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => { headingRef.current?.focus(); }, []);

  return (
    <div className="daily-pulse-missed-days" data-testid="daily-pulse-missed-days">
      <header className="daily-pulse-missed-days__header">
        <div>
          <span className="daily-pulse-missed-days__eyebrow">Daily Pulse</span>
          <h2 ref={headingRef} tabIndex={-1}>Choose a missed day</h2>
        </div>
        <button type="button" className="daily-pulse-missed-days__close" aria-label="Cancel choosing a missed day" onClick={onCancel}>
          <X size={18} aria-hidden="true" />
        </button>
      </header>
      <p className="daily-pulse-missed-days__intro">Add what you remember from another day this week.</p>
      {failedDate ? (
        <div className="daily-pulse-missed-days__error" role="alert">
          <p>We couldn’t open this Daily Pulse. Your previous answers are still here.</p>
          <button type="button" disabled={Boolean(loadingDate)} onClick={() => onSelect(failedDate)}>Try again</button>
        </div>
      ) : null}
      <div className="daily-pulse-missed-days__list" aria-busy={Boolean(loadingDate)}>
        {days.map((day) => (
          <button
            key={day.date}
            type="button"
            className="daily-pulse-missed-days__day"
            disabled={Boolean(loadingDate)}
            aria-label={`Fill Daily Pulse for ${formatDate(day.date)}`}
            onClick={() => onSelect(day.date)}
          >
            <CalendarDays size={20} aria-hidden="true" />
            <span>
              <strong>{formatDate(day.date)}</strong>
              <small>{day.status === 'skipped' ? 'Skipped' : 'Missed'}</small>
            </span>
            <ChevronRight size={18} aria-hidden="true" />
          </button>
        ))}
      </div>
      {loadingDate ? <p className="daily-pulse-missed-days__loading" role="status">Opening Daily Pulse for {formatDate(loadingDate)}…</p> : null}
      <button type="button" className="daily-pulse-missed-days__cancel" onClick={onCancel}>Cancel</button>
    </div>
  );
};
